import {
	Button,
	Divider,
	FormControl,
	FormHelperText,
	IconButton,
	Input,
} from '@mui/material';
import { Editor, useEditorState } from '@tiptap/react';
import React, { useCallback, useState } from 'react';
import { Bold } from '../../../../blocks/icons/Bold';
import { Italic } from '../../../../blocks/icons/Italic';
import { OrderedList } from '../../../../blocks/icons/OrderedList';
import { Underline } from '../../../../blocks/icons/Underline';
import { UnorderedList } from '../../../../blocks/icons/UnorderedList';
import styles from './MuiTipTapMenuElements.module.scss';
import { Icon } from '../../../../blocks/icons';
import MuiModal from '../../../MuiModal/MuiModal';
import { linkProtocols } from '../../useRichTextEditor';

export type MuiTipTapMenuElementsProps = {
	editor: Editor;
	onBlur: () => void;
	onFocus: () => void;
};

const MuiTipTapMenuElements: React.FC<MuiTipTapMenuElementsProps> = ( {
	editor,
	onBlur,
	onFocus,
} ) => {
	const [ open, setOpen ] = useState( false );
	const [ href, setHref ] = useState( '' );
	const [ validationMessage, setValidationMessage ] = useState( '' );

	const linkClick = useCallback( () => {
		const previousUrl = editor.getAttributes( 'link' ).href;
		setHref( previousUrl );
		setOpen( true );
	}, [ editor ] );

	const onClose = useCallback( () => {
		setOpen( false );
		setHref( '' );
	}, [] );

	const validateOffSiteLink = useCallback(
		( href: string ) =>
			linkProtocols.some( ( protocol ) => href.startsWith( protocol ) ),
		[]
	);

	const validate = useCallback(
		( href: string ) => {
			const validationMessageString =
				'Please enter a valid URL, using either: http://, https://, mailto: , or tel:';
			if ( href && !validateOffSiteLink( href ) ) {
				setValidationMessage( validationMessageString );
				return false;
			}
			try {
				new URL( href );
				setValidationMessage( '' );
				return true;
			} catch {
				setValidationMessage( validationMessageString );
				return false;
			}
		},
		[ validateOffSiteLink ]
	);

	const onDone = useCallback( () => {
		if ( validate( href ) ) {
			if ( href === '' ) {
				// empty
				editor.chain().focus().extendMarkRange( 'link' ).unsetLink().run();
			} else {
				// update link
				editor.chain().focus().extendMarkRange( 'link' ).setLink( { href } ).run();
			}
			onClose();
		}
	}, [
		editor,
		href,
		onClose,
		validate
	] );

	const onClear = useCallback( () => {
		setValidationMessage( '' );
		editor.chain().focus().extendMarkRange( 'link' ).unsetLink().run();
		onClose();
	}, [ editor, onClose ] );

	const editorState = useEditorState( {
		editor,
		selector: ( ctx ) => ( {
			isLink: ctx.editor.isActive( 'link' ),
		} ),
	} );

	return (
		<>
			<IconButton
				size='small'
				onClick={ () => editor.chain().focus().toggleBold().run() }
				className={ [ styles.button, editor.isActive( 'bold' ) ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<Bold className={ styles.icon } />
			</IconButton>
			<IconButton
				size='small'
				onClick={ () => editor.chain().focus().toggleItalic().run() }
				className={ [ styles.button, editor.isActive( 'italic' ) ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<Italic className={ styles.icon } />
			</IconButton>
			<IconButton
				size='small'
				onClick={ () => editor.chain().focus().toggleUnderline().run() }
				className={ [ styles.button, editor.isActive( 'underline' ) ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<Underline className={ styles.icon } />
			</IconButton>
			<Divider orientation='vertical' />
			<IconButton
				size='small'
				onClick={ () => editor.chain().focus().toggleBulletList().run() }
				className={ [ styles.button, editor.isActive( 'bulletList' ) ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<UnorderedList className={ styles.icon } />
			</IconButton>
			<IconButton
				size='small'
				onClick={ () => editor.chain().focus().toggleOrderedList().run() }
				className={ [ styles.button, editor.isActive( 'orderedList' ) ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<OrderedList className={ styles.icon } />
			</IconButton>
			<Divider orientation='vertical' />
			<IconButton
				size='small'
				onClick={ linkClick }
				className={ [ styles.button, editorState.isLink ? styles.active : undefined, ].join( ' ' ) }
				onFocus={ onFocus }
				onBlur={ onBlur }
			>
				<Icon type='link' className={ styles.icon } />
			</IconButton>
			<MuiModal
				muiModalProps={ {
					open,
					onClose,
				} }
				title='Configure link'
				description='Enter a URL to link. All URLs must include the http information.'
				controls={
					<>
						<Button variant='outlined' onClick={ onClear }>
							Clear
						</Button>
						<Button onClick={ onDone }>Done</Button>
					</>
				}
			>
				<FormControl fullWidth error={ !!validationMessage } variant='standard'>
					<Input
						fullWidth
						value={ href }
						onChange={ ( e ) => setHref( e.target.value ) }
					/>
					{ validationMessage && (
						<FormHelperText>{ validationMessage }</FormHelperText>
					) }
				</FormControl>
			</MuiModal>
		</>
	);
};

export default MuiTipTapMenuElements;
